import React, { useCallback, useMemo, useState } from "react";
import { useAppDispatch } from "@/services/hooks";
import { Button, Col, Row, UncontrolledTooltip } from "reactstrap";
import "./FilterContainer.css";
import FilterInput from "./FilterInput";
import { selectedFilters as selectedFiltersAction } from "@/services/pageServices/pageAction";

/** One filter chip; FilterInput hands it back with `selected` flipped. */
type FilterChip = {
  label?: string;
  value: string;
  type: string;
  selected: boolean;
  inputChecker?: boolean;
};

const TEMPLATE_TYPES: FilterChip[] = [
  { label: "Category", value: "intro_page", type: "template", selected: false },
  { label: "Home", value: "home_page", type: "template", selected: false },
  { label: "Privacy", value: "privacy_page", type: "template", selected: false },
  { label: "Listing", value: "listing_page", type: "template", selected: false },
  { label: "Product details", value: "product_details", type: "template", selected: false },
  { label: "Contact", value: "contact_form", type: "template", selected: false },
  { label: "Blog index", value: "blog_index", type: "template", selected: false },
  { label: "blog details", value: "blog_page", type: "template", selected: false },
  { label: "Author", value: "author_page", type: "template", selected: false },
  { label: "info", value: "info_article", type: "template", selected: false },
  { label: "Redirection", value: "redirect_page", type: "template", selected: false },
  { label: "Error", value: "error_page", type: "template", selected: false },
  { label: "Sitemap", value: "sitemap_page", type: "template", selected: false },
  { label: "How we score", value: "score_method_page", type: "template", selected: false },
];

const SETUP_TYPES: FilterChip[] = [
  { label: "Published", value: "published", type: "setup", selected: false },
  { label: "Unpublished", value: "unpublished", type: "setup", selected: false },
  { label: "include nav/footer", value: "nav_footer", type: "setup", selected: false },
  { label: "exclude nav/footer", value: "ex_nav_footer", type: "setup", selected: false },
  // SCRUM-1574: no exclusive partner, toggles on its own like a template chip.
  { label: "No index", value: "noindex", type: "setup", selected: false },
];

const ORDER_TYPES: FilterChip[] = [
  { label: "Up", value: "up", type: "order", selected: false },
  { label: "Down", value: "down", type: "order", selected: false },
];

const RECENTLY_ADDED: FilterChip = { value: "recently_added", selected: false, type: "recently_added", inputChecker: true };
const RECENTLY_UPDATED: FilterChip = { value: "recently_updated", selected: false, type: "recently_updated", inputChecker: true };

const FilterContainer = () => {
  const dispatch = useAppDispatch();

  const [showMoreIndex, setShowMoreIndex] = useState(6);
  const [filters, setFilters] = useState<string[]>([]);
  const [templateTypes, setTemplateTypes] = useState(TEMPLATE_TYPES);
  const [setupTypes, setSetupTypes] = useState(SETUP_TYPES);
  const [orderTypes, setOrderTypes] = useState(ORDER_TYPES);
  const [recentlyAdded, setRecentlyAdded] = useState(RECENTLY_ADDED);
  const [recentlyUpdated, setRecentlyUpdated] = useState(RECENTLY_UPDATED);

  const syncFilters = useCallback(
    (next: string[]) => {
      setFilters(next);
      dispatch(selectedFiltersAction(next));
    },
    [dispatch]
  );

  const toggleExclusive = (arr: FilterChip[], on: string, off: string) =>
    arr.map((e) => (e.value === off ? { ...e, selected: false } : e.value === on ? { ...e, selected: true } : e));

  const pushInFilters = useCallback(
    (elem: FilterChip) => {
      let nextFilters = [...filters];

      if (elem.type === "setup" || elem.type === "order") {
        switch (elem.value) {
          case "published":
            setSetupTypes((a) => toggleExclusive(a, "published", "unpublished"));
            nextFilters = nextFilters.filter((f) => f !== "unpublished");
            break;
          case "unpublished":
            setSetupTypes((a) => toggleExclusive(a, "unpublished", "published"));
            nextFilters = nextFilters.filter((f) => f !== "published");
            break;
          case "nav_footer":
            setSetupTypes((a) => toggleExclusive(a, "nav_footer", "ex_nav_footer"));
            nextFilters = nextFilters.filter((f) => f !== "ex_nav_footer");
            break;
          case "ex_nav_footer":
            setSetupTypes((a) => toggleExclusive(a, "ex_nav_footer", "nav_footer"));
            nextFilters = nextFilters.filter((f) => f !== "nav_footer");
            break;
          case "up":
            setOrderTypes((a) => toggleExclusive(a, "up", "down"));
            nextFilters = nextFilters.filter((f) => f !== "down");
            break;
          case "down":
            setOrderTypes((a) => toggleExclusive(a, "down", "up"));
            nextFilters = nextFilters.filter((f) => f !== "up");
            break;
          default:
            break;
        }
      }

      nextFilters = [elem.value, ...nextFilters.filter((f) => f !== elem.value)];
      syncFilters(nextFilters);
    },
    [filters, syncFilters]
  );

  const removeFromFilters = useCallback(
    (elem: FilterChip) => {
      const next = filters.filter((f) => f !== elem.value);
      syncFilters(next);
    },
    [filters, syncFilters]
  );

  const handleCheckResult = useCallback(
    (elem: FilterChip) => {
      if (elem.type === "template") {
        setTemplateTypes((arr) => arr.map((e) => (e.value === elem.value ? { ...e, selected: elem.selected } : e)));
      } else if (elem.type === "setup") {
        setSetupTypes((arr) => arr.map((e) => (e.value === elem.value ? { ...e, selected: elem.selected } : e)));
      } else if (elem.type === "order") {
        setOrderTypes((arr) => arr.map((e) => (e.value === elem.value ? { ...e, selected: elem.selected } : e)));
      } else if (elem.type === "recently_added") {
        setRecentlyAdded({ ...recentlyAdded, selected: elem.selected });
      } else if (elem.type === "recently_updated") {
        setRecentlyUpdated({ ...recentlyUpdated, selected: elem.selected });
      }

      if (elem.selected) {
        pushInFilters(elem);
      } else {
        removeFromFilters(elem);
      }
    },
    [recentlyAdded, recentlyUpdated, pushInFilters, removeFromFilters]
  );

  const handleCleanFilters = useCallback(() => {
    setTemplateTypes(TEMPLATE_TYPES.map((e) => ({ ...e, selected: false })));
    setSetupTypes(SETUP_TYPES.map((e) => ({ ...e, selected: false })));
    setOrderTypes(ORDER_TYPES.map((e) => ({ ...e, selected: false })));
    setRecentlyAdded({ ...RECENTLY_ADDED, selected: false });
    setRecentlyUpdated({ ...RECENTLY_UPDATED, selected: false });
    syncFilters([]);
  }, [syncFilters]);

  const templateSlice = useMemo(() => templateTypes.slice(0, showMoreIndex), [templateTypes, showMoreIndex]);

  return (
    <div className="filter-container container">
      <Row>
        <Col md={filters.length > 0 ? "9" : "12"}>
          <h4 className="text-primary">Filter By</h4>
          <Row>
            <Col>
              <Row className="margin-filters text-primary">Template type</Row>
              {templateSlice.map((elem) => (
                <FilterInput key={elem.value} elem={elem} checkBoxResult={handleCheckResult} />
              ))}
              <Row className="margin-filters">
                {showMoreIndex < templateTypes.length ? (
                  <div className="text-primary" onClick={() => setShowMoreIndex(showMoreIndex + 6)} style={{ cursor: "pointer" }}>
                    <i className="ni ni-bold-down" />
                    <span>
                      <strong>Others</strong>
                    </span>
                  </div>
                ) : (
                  <div className="text-primary" onClick={() => setShowMoreIndex(6)} style={{ cursor: "pointer" }}>
                    <i className="ni ni-bold-up" />
                    <span>
                      <strong>Less</strong>
                    </span>
                  </div>
                )}
              </Row>
            </Col>

            <Col>
              <Row className="margin-filters text-primary">Setup</Row>
              {setupTypes.map((elem) => (
                <FilterInput key={elem.value} elem={elem} checkBoxResult={handleCheckResult} />
              ))}

              <Row className="margin-filters text-primary">Sort By Date</Row>
              {orderTypes.map((elem) => (
                <FilterInput key={elem.value} elem={elem} checkBoxResult={handleCheckResult} />
              ))}
            </Col>

            <Col>
              <Row className="margin-filters text-primary">Recently added</Row>
              <FilterInput elem={recentlyAdded} checkBoxResult={handleCheckResult} />
              <Row className="margin-filters text-primary">Recently updated</Row>
              <FilterInput elem={recentlyUpdated} checkBoxResult={handleCheckResult} />
            </Col>
          </Row>
        </Col>

        {filters.length > 0 && (
          <>
            <Col md="2">
              <Row className="margin-filters">
                <h4 className="text-primary">Current : {filters.length}</h4>
              </Row>
              {filters.map((f) => (
                <Row className="margin-filters" key={f}>
                  <li>{f.split("_").join(" ")}</li>
                </Row>
              ))}
            </Col>
            <Col md="1">
              <Button color="danger" size="sm" id="buttnRemoveFilter" data-placement="top" onClick={handleCleanFilters}>
                <i className="ni ni-fat-remove fa-2x" />
              </Button>
              <UncontrolledTooltip delay={0} placement="top" target="buttnRemoveFilter">
                Remove Current Filter
              </UncontrolledTooltip>
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};

export default FilterContainer;
